// TransDateTimePicker.js
import React from 'react';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TextField, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components to match your existing form styling
const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
}));

const StyledLabel = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 600,
  // marginBottom: '8px',
  color: theme.palette.primary.main,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: theme.palette.background.paper,
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '12px',
  marginTop: '4px',
  marginLeft: '14px',
}));

const TransDateTimePicker = ({
  name,
  label,
  title,
  placeholder,
  value,
  onChange,
  isDisabled = false,
  disabled = false,
  errors,
  width = '100%',
  sx = {},
  inputFormat = 'dd/MM/yyyy hh:mm a',
  disablePast = false,
  minDateTime,
  ...rest
}) => {
  // Convert string value to Date object if needed
  const dateValue = value ? (typeof value === 'string' ? new Date(value) : value) : null;

  const handleDateChange = (newValue) => {
    // Convert Date object to ISO string for consistency
    const dateString = newValue ? newValue.toISOString() : '';
    onChange(name, dateString);
  };

  const isFieldDisabled = isDisabled || disabled;
  const hasError = errors?.status;

  // Function to disable past dates and times
  const shouldDisableDateTime = (dateTime) => {
    if (!disablePast) return false;
    
    const now = new Date();
    const minDate = minDateTime ? new Date(minDateTime) : now;
    
    return dateTime < minDate;
  };

  return (
    <StyledBox sx={{ width, ...sx }}>
      {(label || title) && (
        <StyledLabel variant="body2">
          {title || label}<Typography component="span" sx={{ color: 'red', ml: '4px' }}>
              *
            </Typography>
        </StyledLabel>
      )}
      
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DateTimePicker
          value={dateValue}
          onChange={handleDateChange}
          disabled={isFieldDisabled}
          inputFormat={inputFormat}
          disablePast={disablePast}
          minDateTime={minDateTime}
          shouldDisableTime={shouldDisableDateTime}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              name={name}
              placeholder={placeholder}
              error={hasError}
              fullWidth
              size="small"
              InputProps={{
                ...params.InputProps,
              }}
            />
          )}
          {...rest}
        />
      </LocalizationProvider>
      
      {hasError && (
        <ErrorMessage variant="caption">
          {errors.message}
        </ErrorMessage>
      )}
    </StyledBox>
  );
};

export default TransDateTimePicker;